import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import client from '../../../lib/feathers';

/**
 * Drill-down for a user-data count on the libation review page. Click
 * "sips 5" on a vintage → this modal opens, queries admin/sip filtered
 * by libationVintageId (or $in for libation-wide), and lists who owns
 * which records.
 *
 * Admin-only on the calling side; the underlying services
 * (admin/sip, admin/cellar-entry, admin/libation-label-scan) are
 * isAdmin-gated since v1.78.0.
 *
 * Props:
 *   open: bool
 *   onClose: () => void
 *   type: 'sips' | 'cellar' | 'scans'
 *   libationVintageId?: uuid    (for per-vintage drill-down)
 *   lvIds?: string[]            (for libation-wide drill-down)
 *   title: string               (display label for the modal heading)
 */

const TYPE_TO_SERVICE = {
  sips:   { path: 'admin/sip',                 dateKey: 'sipDate' },
  cellar: { path: 'admin/cellar-entry',        dateKey: 'createdAt' },
  scans:  { path: 'admin/libation-label-scan', dateKey: 'createdAt' },
};

function fmtDate(d) {
  if (!d) return '—';
  try {
    return format(new Date(d), 'MMM d, yyyy');
  } catch (_) {
    return '—';
  }
}

function UserCell({ row, userMap }) {
  // The cellar-entry / sip / scan hooks don't always populate the
  // owning user inline — `getCellarEntryRelated` only joins the
  // location's user, not the entry's. Fall back to looking up by
  // userId via the batch fetched into userMap. Last resort: link to
  // the userId so the operator can still navigate.
  const inline = row.user || row.User || row.owner;
  const fromMap = userMap && row.userId ? userMap[row.userId] : null;
  const u = inline || fromMap;
  if (u) {
    const name = `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.username || u.email || u.id;
    return (
      <Link to={`/admin/users/${u.id}`} className="text-primary hover:underline">
        {name}
      </Link>
    );
  }
  if (row.userId) {
    return (
      <Link to={`/admin/users/${row.userId}`} className="text-amber-700 hover:underline" title="User record could not be loaded">
        {row.userId.slice(0, 8)}…
      </Link>
    );
  }
  return <span className="text-red-600 italic">no userId on row</span>;
}

function SipRow({ row, userMap }) {
  return (
    <tr className="border-b border-gray-100">
      <td className="px-3 py-2"><UserCell row={row} userMap={userMap} /></td>
      <td className="px-3 py-2 text-sm">
        {row.rating == null ? <span className="text-gray-400">—</span> : (
          <span className="font-mono">{Number(row.rating).toFixed(2)}</span>
        )}
      </td>
      <td className="px-3 py-2 text-sm text-gray-600">{fmtDate(row.sipDate || row.createdAt)}</td>
      <td className="px-3 py-2 text-sm text-gray-500 truncate max-w-xs" title={row.notes || ''}>
        {row.notes ? row.notes : <span className="text-gray-300">—</span>}
      </td>
    </tr>
  );
}

function CellarRow({ row, userMap }) {
  return (
    <tr className="border-b border-gray-100">
      <td className="px-3 py-2"><UserCell row={row} userMap={userMap} /></td>
      <td className="px-3 py-2 text-sm">{row.status || '—'}</td>
      <td className="px-3 py-2 text-sm font-mono">{row.quantity ?? '—'}</td>
      <td className="px-3 py-2 text-sm text-gray-600">{fmtDate(row.createdAt)}</td>
    </tr>
  );
}

function ScanRow({ row, userMap }) {
  return (
    <tr className="border-b border-gray-100">
      <td className="px-3 py-2"><UserCell row={row} userMap={userMap} /></td>
      <td className="px-3 py-2 text-sm">{row.matchResult || row.status || '—'}</td>
      <td className="px-3 py-2 text-sm text-gray-600">{fmtDate(row.createdAt)}</td>
      <td className="px-3 py-2 text-sm">
        {row.id && (
          <Link to={`/admin/reports/bottle-scans/${row.id}`} className="text-primary hover:underline">
            view scan
          </Link>
        )}
      </td>
    </tr>
  );
}

const HEADERS = {
  sips:   ['User', 'Rating', 'Date', 'Notes'],
  cellar: ['User', 'Status', 'Qty',  'Added'],
  scans:  ['User', 'Result', 'When', ''],
};

const ROW_COMPONENT = {
  sips: SipRow,
  cellar: CellarRow,
  scans: ScanRow,
};

export default function UserDataDrillDownModal({ open, onClose, type, libationVintageId, lvIds, title }) {
  const [rows, setRows] = useState(null);
  const [userMap, setUserMap] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    const svc = TYPE_TO_SERVICE[type];
    if (!svc) {
      setError(`No drill-down configured for type "${type}"`);
      return;
    }

    const load = async () => {
      setRows(null);
      setUserMap({});
      setError(null);
      try {
        const query = { $limit: 200 };
        if (libationVintageId) {
          query.libationVintageId = libationVintageId;
        } else if (lvIds && lvIds.length) {
          query.libationVintageId = { $in: lvIds };
        }
        const result = await client.service(svc.path).find({ query });
        const fetched = result.data || [];
        setRows(fetched);

        // Batch-fetch owning users — none of the related hooks
        // populate the entry's own user inline (only the cellar
        // location's user, for cellar entries). Hit admin/user once
        // with $in to map userId → user record.
        const userIds = Array.from(new Set(fetched.map((r) => r.userId).filter(Boolean)));
        if (userIds.length === 0) return;
        const usersResult = await client.service('admin/user').find({
          query: { id: { $in: userIds }, $limit: 200 },
        });
        const users = (usersResult.data || usersResult || []);
        const map = {};
        for (const u of users) map[u.id] = u;
        setUserMap(map);
      } catch (e) {
        console.error('UserDataDrillDownModal error', e);
        setError(e.message || 'Load failed');
      }
    };
    load();
  }, [open, type, libationVintageId, lvIds]);

  if (!open) return null;

  const headers = HEADERS[type] || [];
  const RowComponent = ROW_COMPONENT[type];

  return (
    <div className="fixed inset-0 z-20 overflow-y-auto">
      <div className="fixed inset-0 bg-gray-500 opacity-75" onClick={onClose} />
      <div className="relative max-w-3xl mx-auto my-12 bg-white rounded-lg shadow-xl p-6">
        <div className="flex justify-between items-start mb-3">
          <h3 className="text-lg font-medium text-gray-900">{title}</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700">✕</button>
        </div>

        {error && <div className="text-sm text-red-700">{error}</div>}
        {!error && rows === null && <div className="text-sm text-gray-500">Loading…</div>}
        {!error && rows && rows.length === 0 && (
          <div className="text-sm text-gray-500 italic">No records.</div>
        )}
        {!error && rows && rows.length > 0 && (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                {headers.map((h) => <th key={h} className="px-3 py-2">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => <RowComponent key={row.id || idx} row={row} userMap={userMap} />)}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
